import React, {useState, useEffect, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import {BookedMeeting, CalEventInterface} from "../../interfaces/common";
import CustomButton from "../CustomButton"
import Modal from "../Modal"
import ModalHeader from "../Modal/components/ModalHeader"
import ModalBody from "../Modal/components/ModalBody"
import ModalFooter from "../Modal/components/ModalFooter"

import {
  addMinutes,
  dateFormatDate,
  dateFormatHourMinute,
} from "../../helpers/dateFunctions";
import iconCalendar from "url:../../assets/icons/calendar.svg"
import iconClock from "url:../../assets/icons/clock.svg";
import iconBadgeCheck from "url:../../assets/icons/badge-check.svg";
import iconClose from "url:../../assets/icons/close.svg";
import Api from "../../services/Api";
import DatePicker from "../DatePicker";
import CheckBox from "../Boxes/CheckBox";

interface AvailInterface {
  from: Date
  to: Date
}

interface TimeSlot {
  start: string
  end: string
  startDate: Date
  endDate: Date
}

interface Props {
  meeting?: BookedMeeting;
  who: string
  clientId: string;
  close: Function
  lang: string
}

const BookMeeting: React.FC<Props> = ({ meeting, clientId, close, lang, who }) => {
  const api = Api.getInstance();
  const translation = useTranslation();
  const { t } = translation;
  const [txt, setTxt] = useState<string>('');
  const [booked, setBooked] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [newMeeting, setNewMeeting] = useState<CalEventInterface|null>(null)
  const [avails, setAvails] = useState<Array<AvailInterface>>([]);
  const [bookableDays, setBookableDays] = useState<Array<Date>>([]);
  const [selectedDate, setSelectedDate] = useState<Date|null>(null)
  const [clientAgreed, setClientAgreed] = useState<boolean>(who === 'client')
  const [error, setError] = useState<string|null>(null)
  const minutesBetweenMeetings = useRef<number>(15)

  useEffect(() => {
    const meetingId = meeting ? meeting.id : '0'
    if (who === 'coach') {
      api.get('coach/availability/all/' + clientId + '/' + meetingId).then((response) => {
        const newAvails: Array<AvailInterface> = []
        setError(response.json.error)
        const newBookableDays: Array<Date> = []
        for (const recurring of response.json.available) {
          const from = new Date(recurring.from)
          const to = new Date(recurring.to)
          newBookableDays.push(from)
          newAvails.push({
            from: from,
            to: to
          })
        }
        minutesBetweenMeetings.current = response.json.startInterval
        setAvails(newAvails)
        setBookableDays(newBookableDays)
      })
    } else {
      api.get('client/meeting_availability').then((response) => {
        if (response.json.error) {
          setError(response.json.error)
        } else {
          const newAvails: Array<AvailInterface> = []
          const newBookableDays: Array<Date> = []
          for (const timeslot of response.json.timeslots as Array<TimeSlot>) {
            const from = new Date(timeslot.start)
            const to = new Date(timeslot.end)
            newBookableDays.push(from)
            newAvails.push({
              from: from,
              to: to
            })
          }
          minutesBetweenMeetings.current = response.json.startInterval
          setAvails(newAvails)
          setBookableDays(newBookableDays)
        }
      })
    }

  }, [])

  const doBook = () => {
    if (who === 'coach') {
      const data = {
        id: meeting?.id,
        client_id: meeting?.clientId,
        from: newMeeting?.start,
        length: meeting?.length,
        message: txt
      }
      api.post('coach/meeting/book', data).then(response => {
        setBooked(true);
      })
    } else {
      const data = {
        from: newMeeting?.start,
        length: meeting?.length,
        id: meeting?.id,
      }

      api.post('client/meeting_book', data).then(response => {
        setBooked(true);
      })
    }

  }

  const getBookableMeetings = (day: Date) => {
    const startEvents: Array<CalEventInterface> = []
    let id = 1
    const meetingInSeconds = meeting ? meeting.length * 60 : 1800
    const pauseInSeconds = minutesBetweenMeetings.current * 60
    for (const d of avails) {
      if (d.from.getFullYear() != day.getFullYear() || d.from.getMonth() != day.getMonth() || d.from.getDate() != day.getDate()) {
        continue
      }
      let start = new Date(d.from)
      while ((d.to.getTime() - start.getTime()) / 1000 >= meetingInSeconds) {
        let end = new Date(start)
        end.setSeconds(end.getSeconds() + meetingInSeconds)
        startEvents.push({
          id: id,
          title: "M",
          start: start,
          end: end
        })
        console.log('Adding ' + start + ' - ' + end)
        id++
        start = new Date(start)
        start.setSeconds(start.getSeconds() + pauseInSeconds)
      }

    }
    return startEvents
  }

  const showContent = () => {
    if (booked) {
      return (
        <div>
          <div className="my-12 flex justify-center items-center">
            <img className="icon-xl" alt="check" src={iconBadgeCheck} />
          </div>
          <div className="text-neutral-300">
            {t("your-meeting-has")} {meeting?.id === '0' ? t('been-booked') : t('been-changed')} {who === 'coach' ? t("client-is-notified") : t("coach-is-notified")}
          </div>
        </div>
      )
    } else if (newMeeting) {
      return (
        <div>
          <div className="font-medium">
            {meeting?.firstName} {meeting?.lastName}
          </div>
          <div className="mt-4 flex items-center">
            <img alt="calendar" className="h-6 w-6" src={iconCalendar}/>
            <div className="ml-2">{dateFormatDate(newMeeting.start, false, lang)}</div>
          </div>
          <div className="mt-4 flex items-center">
            <img className="h-6 w-6" alt="Time" src={iconClock}/>
            <span className="ml-2">
                {dateFormatHourMinute(newMeeting.start)} - {dateFormatHourMinute(newMeeting.end)}
              </span>
          </div>

          {
            who === 'coach' &&
              <div className="mt-4 flex items-center">
                  <div onClick={() => setClientAgreed(!clientAgreed)}>
                      <CheckBox checked={clientAgreed}/>
                  </div>
                  <span className="ml-2">
                    {lang === 'sv' ? 'Avstämt med klient' : 'Confirmed with client'}
                  </span>
              </div>
          }

          <div className="mt-4">
            <textarea value={txt} className="z-input z-700 h-24"
                      placeholder={who === 'coach' ? t("write-message-to-client") : t("write-message-to-coach")}
                      onChange={(e) => setTxt(e.target.value)} autoComplete="off"/>
          </div>
        </div>
      )
    } else {
      return <></>
    }
  }

  const selectMeetingTime = (e: CalEventInterface) => {
    setNewMeeting(e)
  }

  useEffect(() => {
    if (newMeeting) {
      setConfirm(true)
    }
  }, [newMeeting])

  const showConfirmText = () => {
    if (booked) {
      if (meeting?.id === '0') {
        if (lang === 'sv') {
          return "Mötet har bokats"
        } else {
          return "The meeting has been booked"
        }
      } else {
        if (lang === 'sv') {
          return "Mötet har ändrats"
        } else {
          return "The meeting has been changed"
        }
      }
    } else {
      if (meeting?.id === '0') {
        if (lang === 'sv') {
          return "Bekräfta bokning"
        } else {
          return "Confirm meeting"
        }
      } else {
        if (lang === 'sv') {
          return "Bekräfta ändring"
        } else {
          return "Confirm change"
        }
      }
    }
  }

  if (error) {
    if (error === 'no_remaining_meetings') {
      return (
        <>
          <div>
            {
              lang === 'sv' ? 'Du har bokat alla dina möten' : 'You have booked all your meetings'
            }
          </div>
        </>
      )
    }
    return (
      <div>{error}</div>
    )
  }

  return (
    <>
      <Modal isOpen={confirm} onClose={() => setConfirm(false)}>
        <ModalHeader onClose={() => setConfirm(false)}>
          {showConfirmText()}
        </ModalHeader>
        <ModalBody>
          <>
            {showContent()}
          </>
        </ModalBody>
        <ModalFooter>
          {
            booked ? (
              <CustomButton buttonType="blueBase" className="w-full" onClick={() => close(booked)}>Ok</CustomButton>
            ) : (
              <CustomButton buttonType="blueBase" className="w-full" onClick={() => doBook()} disabled={!clientAgreed}>
                {meeting?.id === '0' ? t('book-meeting') : t('change-meeting')}
              </CustomButton>
            )
          }
        </ModalFooter>
      </Modal>


      {
        meeting?.id != '0' &&
          <>
            <div className="text-2xl mt-4 font-medium">
              {lang === 'sv' ? 'Ändra datum och tid' : 'Change date and time'}
            </div>
            <div className="mt-4 p-4 bg-neutral-700 text-white rounded border border-neutral-500">
                <div className="flex justify-betwen items-center">
                    <div className="font-medium">
                      {t("original-booking")}
                    </div>
                    <div className="flex flex-grow justify-end">
                        <img className="cursor-pointer" src={iconClose} alt="close"
                             onClick={() => close(false)}/>
                    </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                    <div>
                      {meeting?.firstName} {meeting?.lastName}
                    </div>
                    <div className="ml-4 flex">
                        <img src={iconCalendar} className="icon" alt="cal"/>
                        <span className="ml-1">
                          {meeting && dateFormatDate(meeting.startDate, false, lang)}
                        </span>
                    </div>
                    <div className="ml-4 flex">
                        <img src={iconClock} className="icon" alt="cal"/>
                        <span className="ml-1">
                          {meeting && dateFormatHourMinute(meeting.startDate)} - {meeting && dateFormatHourMinute(addMinutes(meeting.startDate, meeting.length))}
                        </span>
                    </div>
                </div>
            </div>
            <div className="mt-8">&nbsp;</div>
          </>
      }


      <DatePicker dates={bookableDays} clickDate={(d: Date) => setSelectedDate(d)}
                  preSelectedDate={null}/>

      {
        selectedDate &&
          <div className="mt-8">
              <div className="flex justify-between items-end">
                  <div className="font-medium text-lg">{t('available-times')}</div>
                  <div>{dateFormatDate(selectedDate, false, lang)}</div>
              </div>

              <div className="grid grid-cols-3 gap-x-2">
                {
                  getBookableMeetings(selectedDate).map((d, i) => {
                    return (
                      <div key={"time_" + i} className="mt-2">
                        <CustomButton buttonType="outlineWhiteBase" onClick={() => selectMeetingTime(d)}
                                      className="w-full">
                          {dateFormatHourMinute(d.start)}
                        </CustomButton>
                      </div>
                    )
                  })
                }
              </div>

          </div>
      }
    </>
  );
};

export default BookMeeting;
